import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const RevisedForm941Form2024 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Revised IRS Form 941 for 2024: Deadlines & Changes"
          description="New tax limits & COVID credit removal! Learn about the revised 2024 Form 941 deadlines, download a copy & easily e-file with TaxZerone in 3 steps."
          keywords="Form 941, Payroll tax filing, IRS Form 941, Employment tax, Quarterly tax reporting"
          canonical="resources/employment-tax/irs-form-941-2024-updates/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Revised IRS Form 941</span>
          </span>{' '}
          for 2024
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/irs-form-941-2024-updates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941: 2024 Updates at a Glance
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1.6 }}>
                    <b>Form 941</b>
                    {
                      ' is the Employer’s Quarterly Federal Tax Return used by employers to report federal income tax withheld and Social Security and Medicare taxes. The '
                    }
                    <b>2024 revision</b>
                    {
                      ' removed several COVID-19-related lines and updated the form to reflect the end of pandemic-related tax credits.'
                    }
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1.6 }}>
                    <b>Key 2024 Changes:</b>
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <b>Social Security wage base:</b>
                        {
                          ' $168,600, with a 6.2% tax rate for both employers and employees. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Medicare tax:</b>
                        {
                          ' Remains at 1.45% for both employers and employees, with no wage base limit. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 941-SS and Form 941-PR:</b>
                        {
                          ' Discontinued after Q4 2023; employers in U.S. territories generally use Form 941 starting in 2024. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>COVID-19 credit lines:</b>
                        {
                          ' Several lines related to pandemic-era sick and family leave credits were removed. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 8974:</b>
                        {
                          ' Line 11 was revised to report the payroll tax credit for qualified small businesses conducting research activities. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Part 3:</b>
                        {
                          ' Lines 19–28 were removed as pandemic-related provisions ended. '
                        }
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      As a business owner, one of your quarterly
                      responsibilities is completing Federal Form 941. To be
                      compliant and avoid IRS penalties, you must{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-941/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 941
                      </Link>{' '}
                      every quarter without fail.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      As the COVID-19 pandemic has officially ended, the IRS has
                      revised{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941
                      </Link>{' '}
                      for 2024 to reflect the conclusion of pandemic-related tax
                      relief programs. The new version removes several lines
                      that were added specifically to account for COVID-related
                      tax credits, such as the Employee Retention Credit and
                      sick/family leave credits. These changes simplify the
                      filing process for employers moving forward.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Here, we’ll walk you through the key updates to{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-941/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 941
                      </Link>{' '}
                      for 2024, important filing deadlines, and the steps to
                      ensure compliance with the revised tax requirements.
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Key Changes in IRS Form 941 for 2024
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The 2024 version of the form has been revised to accommodate
                  the following updates:
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Social Security and Medicare Tax for 2024:
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 4 }}>
                  <li>
                    The Social Security tax rate on taxable wages is 6.2% for
                    both the employer and employee. The wage base limit for
                    Social Security is $168,600.
                  </li>
                  <li>
                    The Medicare tax rate remains unchanged at 1.45% for both
                    the employee and employer. There is no wage base limit for
                    Medicare tax.
                  </li>
                  <li>
                    Social Security and Medicare taxes apply to the wages of
                    household workers you pay $2,700 or more in cash wages in
                    2024.
                  </li>
                  <li>
                    Social Security and Medicare taxes also apply to election
                    workers who are paid $2,300 or more in cash or equivalent
                    compensation in 2024.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  New Form 941 (sp):
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li>
                    Form 941-SS, Employer’s QUARTERLY Federal Tax Return for
                    American Samoa, Guam, the Commonwealth of the Northern
                    Mariana Islands, and the U.S. Virgin Islands, as well as
                    Form 941-PR, Planilla para la DeclaraciÃ³n Federal
                    TRIMESTRAL del Patrono, were discontinued after the fourth
                    quarter of 2023. Starting in 2024, employers in U.S.
                    territories should file Form 941. If you prefer the form and
                    instructions in Spanish, you can file the new Form 941 (sp).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Below are the lines that have been removed from 941 form 2024:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    In Part 1, Lines 11a to 11g have been removed. Instead, Line
                    11 will now include information regarding Form 8974 (Payroll
                    Tax Credit for Qualified Small Businesses for Increasing
                    Research Activities). Refer to the image attached below.
                  </li>

                  <picture>
                    <source srcSet="/assets/images/941-employmen-tax-2023.webp" />
                    <source srcSet="/assets/images/941-employmen-tax-2023.png" />
                    <img
                      src="/assets/images/941-employmen-tax-2023.png"
                      alt="Lines 11a to 11g removed from Form 941 for the tax year 2024"
                      width={600}
                      height={200}
                      className="new-form-employ"
                    />
                  </picture>

                  <li
                    style={{
                      marginBottom: 16,
                      marginTop: 10,
                      listStyleType: 'none',
                    }}
                  >
                    Form 941, 2023
                  </li>

                  <picture>
                    <source srcSet="/assets/images/941-employmen-tax-2024.webp" />
                    <source srcSet="/assets/images/941-employmen-tax-2024.png" />
                    <img
                      src="/assets/images/941-employmen-tax-2024.png"
                      alt="Revised Form 941 for the 2024 tax year"
                      width={600}
                      height={67}
                      className="new-form-employ"
                    />
                  </picture>
                  <li
                    style={{
                      marginBottom: 16,
                      marginTop: 10,
                      listStyleType: 'none',
                    }}
                  >
                    Revised Form 941, 2024
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Lines 13a to 13i have also been removed. Instead, Line 13
                    will now include information on the total deposits for the
                    quarter, including overpayments applied from a prior quarter
                    and overpayments from Form 941-X, Form 941-X (PR), Form
                    944-X, or Form 944-X (SP) filed in the current quarter.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/941employment-remove.webp" />
                    <source srcSet="/assets/images/941employment-remove.png" />
                    <img
                      src="/assets/images/941employment-remove.png"
                      alt="Lines 13a to 13i has also been removed in the revised form for tax year 2024"
                      width={600}
                      height={384}
                      className="new-form-employ"
                    />
                  </picture>
                  <li style={{ marginBottom: 16, marginTop: 10 }}>
                    In Part 3, Lines 19 to 28 have been removed.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/941-employment-new-2024.webp" />
                    <source srcSet="/assets/images/941-employment-new-2024.png" />
                    <img
                      src="/assets/images/941-employment-new-2024.png"
                      alt="Form 941 2024: In Part 3, Lines 19 to 28 have been removed."
                      width={600}
                      height={67}
                      className="new-form-employ"
                    />
                  </picture>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What are the Form 941 quarterly deadlines for the 2024 tax
                  year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 941 is due by the last day of the month following the end
                  of each quarter. For example, wages paid during the 1st
                  quarter{' '}
                  <span style={{ fontWeight: 500, fontStyle: 'italic' }}>
                    (January through March)
                  </span>{' '}
                  must generally be reported by <b>April 30</b>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The deadlines for 2024 941 Form Filings are listed below:
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Reporting Quarter
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Q-1 (January - March)</TableCell>

                          <TableCell>April 30, 2024</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Q-2 (April - June)</TableCell>

                          <TableCell>July 31, 2024</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Q-3 (July - September)</TableCell>

                          <TableCell>October 31, 2024</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Q-4 (October - December)</TableCell>

                          <TableCell>January 31, 2025</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> If you have made timely deposits covering your
                  full tax liability for the quarter, you may file by the 10th
                  day of the second month following the end of the quarter. For
                  example, if you fully paid your taxes for the 1st quarter on
                  time, you can file Form 941 by May 10.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-file Form 941 for the 2024 Tax Year with TaxZerone?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Follow the simple steps outlined below to complete and submit
                  your forms to the IRS in minutes!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1:</b> Sign in or create a free TaxZerone account
                  today!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2:</b> Select Form 941 from the list of forms and
                  enter the details.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3:</b> Review and transmit the return to the IRS.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 941 has undergone substantial modifications for the 2024
                  tax year, including reduced Social Security and Medicare tax
                  limits and the elimination of COVID-19 credits. Employers must
                  meet quarterly filing requirements. Using an IRS-authorized
                  platform, such as TaxZerone, simplifies the process while
                  maintaining accuracy and compliance.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to get started with Form 941 e-filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone has incorporated all the IRS changes in Form 941 for the
              2024 tax year. <br />
              Click the button below and start your filing process with
              TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941revised`}
              >
                E-file Form 941
              </Button>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default RevisedForm941Form2024;
